/**
 * The fixed CSV layouts for every shared record type, and the import / export that use them.
 *
 * Import is forgiving about the file (header spelling and order, delimiter, extra columns, blank rows,
 * spreadsheet number formats) and strict about the data: a row with a bad required value is skipped and
 * reported by row number; a bad optional value is dropped with a warning. Nothing is guessed silently.
 * Export writes the canonical header names, so what we export we can always import again.
 *
 * Pure; safe in Node and the browser.
 */
import { toCsv, type CsvValue } from "../csv";
import { parseCsv } from "./csvRead";
import {
  cleanBool, cleanCurrency, cleanDate, cleanEmail, cleanEnum, cleanInt, cleanNumber, cleanPhone, cleanText,
  cleanTime, cleanTimeZone, cleanUrl, parseNumber, type Cleaned,
} from "./fields";
import { isValidId, newId } from "./ids";
import {
  CANCELLATION_TERMS, EXHIBITOR_STATUSES, EXPENSE_KINDS, GUEST_STATUSES, INCOME_STATUSES, QUOTE_STATUSES, TAX_TYPES, type RecordKind, type RecordMap,
} from "./types";

export type FieldKind =
  | "id" | "text" | "longtext" | "email" | "phone" | "url" | "int" | "number" | "percent" | "bool"
  | "date" | "time" | "currency" | "timezone" | "enum";

export interface ColumnSpec {
  /** Property name on the record. */
  key: string;
  /** Canonical header written on export. */
  header: string;
  kind: FieldKind;
  required?: boolean;
  /** Other header spellings accepted on import. */
  aliases?: readonly string[];
  values?: readonly string[];
  synonyms?: Record<string, string>;
  /** Used when the cell is blank. */
  default?: string | number | boolean;
  /** Smallest allowed value for a number column. */
  min?: number;
}

export interface Layout {
  kind: RecordKind;
  label: string;
  idPrefix: string;
  columns: readonly ColumnSpec[];
}

const id: ColumnSpec = { key: "id", header: "ID", kind: "id", aliases: ["Id", "Reference", "Ref"] };
const notes: ColumnSpec = { key: "notes", header: "Notes", kind: "longtext", aliases: ["Note", "Comments", "Comment", "Remarks"] };

export const LAYOUTS: { [K in RecordKind]: Layout } = {
  guests: {
    kind: "guests", label: "Guests", idPrefix: "g",
    columns: [
      id,
      { key: "name", header: "Name", kind: "text", required: true, aliases: ["Full name", "Guest", "Guest name", "Attendee"] },
      { key: "email", header: "Email", kind: "email", aliases: ["E-mail", "Email address"] },
      { key: "phone", header: "Phone", kind: "phone", aliases: ["Mobile", "Telephone", "Phone number", "Cell"] },
      { key: "organisation", header: "Organisation", kind: "text", aliases: ["Organization", "Company", "Employer"] },
      { key: "role", header: "Role", kind: "text", aliases: ["Title", "Job title", "Position"] },
      {
        key: "status", header: "Status", kind: "enum", values: GUEST_STATUSES, default: "invited", aliases: ["RSVP", "Reply", "Response", "Attendance", "Rsvp status"],
        synonyms: { yes: "confirmed", accepted: "confirmed", attending: "confirmed", going: "confirmed", no: "declined", "not attending": "declined", pending: "invited", "wait list": "waitlist", "checked in": "attended", present: "attended" },
      },
      { key: "dietary", header: "Dietary", kind: "text", aliases: ["Diet", "Dietary needs", "Dietary requirements", "Food"] },
      { key: "access", header: "Access needs", kind: "text", aliases: ["Accessibility", "Access", "Mobility"] },
      { key: "needsStay", header: "Needs stay", kind: "bool", default: false, aliases: ["Needs room", "Accommodation", "Stay", "Hotel needed", "Room", "Hotel", "Needs room?"] },
      { key: "arrival", header: "Arrival", kind: "date", aliases: ["Arrival date", "Arrives"] },
      { key: "departure", header: "Departure", kind: "date", aliases: ["Departure date", "Departs"] },
      { key: "arrivalTime", header: "Arrival time", kind: "time", aliases: ["Lands", "Landing time", "Arrives at", "Flight arrival time"] },
      { key: "arrivalFlight", header: "Arrival flight", kind: "text", aliases: ["Arrival flight number", "Inbound flight", "Flight in", "Arrival service"] },
      { key: "departureTime", header: "Departure time", kind: "time", aliases: ["Takes off", "Departs at", "Flight departure time"] },
      { key: "departureFlight", header: "Departure flight", kind: "text", aliases: ["Departure flight number", "Outbound flight", "Flight out", "Departure service"] },
      { key: "table", header: "Table", kind: "text", aliases: ["Seat", "Table number"] },
      { key: "together", header: "Group", kind: "text", aliases: ["Seat together", "Keep together", "Seating group", "Party", "Seat with"] },
      { key: "apart", header: "Keep apart from", kind: "text", aliases: ["Keep apart", "Seat apart from", "Not with", "Do not seat with", "Avoid"] },
      { key: "plusOnes", header: "Plus ones", kind: "int", aliases: ["Guests", "Plus one", "Additional guests"] },
      notes,
    ],
  },
  sessions: {
    kind: "sessions", label: "Agenda sessions", idPrefix: "s",
    columns: [
      id,
      { key: "title", header: "Title", kind: "text", required: true, aliases: ["Session", "Session title", "Name", "Topic"] },
      { key: "day", header: "Day", kind: "date", required: true, aliases: ["Date"] },
      { key: "start", header: "Start", kind: "time", required: true, aliases: ["Start time", "From"] },
      { key: "end", header: "End", kind: "time", aliases: ["End time", "To", "Until"] },
      { key: "track", header: "Track", kind: "text", aliases: ["Stream", "Category"] },
      { key: "room", header: "Room", kind: "text", aliases: ["Location", "Venue", "Hall"] },
      { key: "presenter", header: "Presenter", kind: "text", aliases: ["Speaker", "Speakers", "Host", "Facilitator"] },
      { key: "link", header: "Link", kind: "url", aliases: ["Join link", "URL", "Stream link", "Meeting link"] },
      { key: "timeZone", header: "Time zone", kind: "timezone", aliases: ["Timezone", "TZ"] },
      notes,
    ],
  },
  vendors: {
    kind: "vendors", label: "Vendors", idPrefix: "v",
    columns: [
      id,
      { key: "name", header: "Name", kind: "text", required: true, aliases: ["Vendor", "Company", "Supplier"] },
      { key: "category", header: "Category", kind: "text", aliases: ["Type", "Service"] },
      { key: "contact", header: "Contact", kind: "text", aliases: ["Contact person", "Contact name"] },
      { key: "email", header: "Email", kind: "email", aliases: ["E-mail", "Email address"] },
      { key: "phone", header: "Phone", kind: "phone", aliases: ["Telephone", "Mobile", "Phone number"] },
      notes,
    ],
  },
  quotes: {
    kind: "quotes", label: "Quotes", idPrefix: "q",
    columns: [
      id,
      { key: "vendor", header: "Vendor", kind: "text", required: true, aliases: ["Supplier", "Company"] },
      { key: "item", header: "Item", kind: "text", required: true, aliases: ["Service", "Description", "Product"] },
      { key: "amount", header: "Amount", kind: "number", required: true, min: 0, aliases: ["Price", "Quote", "Total", "Cost"] },
      { key: "currency", header: "Currency", kind: "currency", aliases: ["Cur"] },
      { key: "validUntil", header: "Valid until", kind: "date", aliases: ["Expires", "Valid to", "Expiry"] },
      {
        key: "status", header: "Status", kind: "enum", values: QUOTE_STATUSES, default: "requested",
        synonyms: { pending: "requested", asked: "requested", sent: "requested", approved: "accepted", won: "accepted", declined: "rejected", lost: "rejected" },
      },
      { key: "rating", header: "Rating", kind: "number", aliases: ["Vendor rating", "Stars"] },
      { key: "depositPct", header: "Deposit %", kind: "percent", aliases: ["Deposit", "Deposit percent"] },
      { key: "cancellation", header: "Cancellation", kind: "enum", values: CANCELLATION_TERMS, aliases: ["Cancellation terms"], synonyms: { easy: "flexible", free: "flexible", partial: "moderate", medium: "moderate", "non-refundable": "strict", nonrefundable: "strict", none: "strict", hard: "strict" } },
      notes,
    ],
  },
  expenses: {
    kind: "expenses", label: "Expenses and income", idPrefix: "e",
    columns: [
      id,
      { key: "date", header: "Date", kind: "date", required: true },
      { key: "description", header: "Description", kind: "text", required: true, aliases: ["Item", "Details", "Memo"] },
      { key: "kind", header: "Type", kind: "enum", values: EXPENSE_KINDS, default: "cost", aliases: ["Kind", "Direction"], synonyms: { expense: "cost", spend: "cost", payment: "cost", revenue: "income", receipt: "income", sale: "income" } },
      { key: "category", header: "Category", kind: "text", aliases: ["Group"] },
      { key: "vendor", header: "Vendor", kind: "text", aliases: ["Supplier", "Paid to", "Payee", "Client"] },
      { key: "amount", header: "Amount", kind: "number", required: true, aliases: ["Total", "Cost", "Price"] },
      { key: "currency", header: "Currency", kind: "currency", aliases: ["Cur"] },
      { key: "taxType", header: "Tax type", kind: "enum", values: TAX_TYPES, default: "none", aliases: ["Tax"], synonyms: { "sales tax": "sales_tax", "no tax": "none", gst: "gst", vat: "vat" } },
      { key: "taxRate", header: "Tax rate %", kind: "percent", aliases: ["Tax rate", "Rate", "Tax %"] },
      { key: "taxIncluded", header: "Tax included", kind: "bool", default: false, aliases: ["Includes tax", "Inclusive"] },
      { key: "status", header: "Income status", kind: "enum", values: INCOME_STATUSES, aliases: ["Payment status", "Pledge status"], synonyms: { received: "paid", settled: "paid", promised: "pledged", committed: "pledged", unpaid: "pledged", outstanding: "pledged" } },
      { key: "receipt", header: "Receipt", kind: "text", aliases: ["Receipt number", "Receipt no", "Invoice number", "Invoice"] },
      notes,
    ],
  },
  rooms: {
    kind: "rooms", label: "Rooming list", idPrefix: "r",
    columns: [
      id,
      { key: "guest", header: "Guest", kind: "text", required: true, aliases: ["Name", "Guest name", "Occupant"] },
      { key: "hotel", header: "Hotel", kind: "text", aliases: ["Property"] },
      { key: "roomType", header: "Room type", kind: "text", aliases: ["Type", "Room"] },
      { key: "checkIn", header: "Check in", kind: "date", required: true, aliases: ["Check-in", "Arrival", "In"] },
      { key: "checkOut", header: "Check out", kind: "date", required: true, aliases: ["Check-out", "Departure", "Out"] },
      { key: "sharedWith", header: "Shared with", kind: "text", aliases: ["Sharing with", "Roommate"] },
      notes,
    ],
  },
  tasks: {
    kind: "tasks", label: "Tasks", idPrefix: "t",
    columns: [
      id,
      { key: "title", header: "Task", kind: "text", required: true, aliases: ["Title", "Item", "To do", "Name", "Description"] },
      { key: "due", header: "Due", kind: "date", aliases: ["Due date", "Deadline", "Date", "By"] },
      { key: "done", header: "Done", kind: "bool", default: false, aliases: ["Complete", "Completed", "Finished", "Status"] },
      { key: "owner", header: "Owner", kind: "text", aliases: ["Assigned to", "Responsible", "Who"] },
      { key: "category", header: "Category", kind: "text", aliases: ["Group", "Area"] },
      notes,
    ],
  },
  exhibitors: {
    kind: "exhibitors", label: "Exhibitors", idPrefix: "x",
    columns: [
      id,
      { key: "company", header: "Company", kind: "text", required: true, aliases: ["Exhibitor", "Name", "Organisation", "Organization", "Business"] },
      { key: "booth", header: "Booth", kind: "text", aliases: ["Stand", "Booth number", "Stand number", "Space"] },
      { key: "contact", header: "Contact", kind: "text", aliases: ["Contact person", "Contact name"] },
      { key: "email", header: "Email", kind: "email", aliases: ["E-mail", "Email address"] },
      { key: "phone", header: "Phone", kind: "phone", aliases: ["Telephone", "Mobile", "Phone number"] },
      {
        key: "status", header: "Status", kind: "enum", values: EXHIBITOR_STATUSES, default: "enquiry",
        synonyms: { lead: "enquiry", inquiry: "enquiry", interested: "enquiry", reserved: "booked", confirmed: "booked", signed: "booked", deposit: "booked", "paid in full": "paid", settled: "paid", withdrawn: "cancelled", declined: "cancelled", cancelled: "cancelled", canceled: "cancelled" },
      },
      { key: "fee", header: "Fee", kind: "number", aliases: ["Booth fee", "Price", "Amount", "Cost"] },
      { key: "paid", header: "Paid", kind: "number", aliases: ["Paid so far", "Received", "Payment received", "Deposit paid"] },
      { key: "currency", header: "Currency", kind: "currency", aliases: ["Cur"] },
      { key: "needs", header: "Needs", kind: "longtext", aliases: ["Requirements", "Requests", "Power and wifi", "Extras"] },
      notes,
    ],
  },
  budgets: {
    kind: "budgets", label: "Budget lines", idPrefix: "b",
    columns: [
      id,
      { key: "category", header: "Category", kind: "text", required: true, aliases: ["Item", "Line", "Name", "Budget line"] },
      { key: "planned", header: "Planned", kind: "number", required: true, aliases: ["Budget", "Budgeted", "Amount", "Allocated"] },
      { key: "currency", header: "Currency", kind: "currency", aliases: ["Cur"] },
      notes,
    ],
  },
};

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");

export type IssueLevel = "error" | "warning";
export interface ImportIssue {
  /** 1-based row number as shown in a spreadsheet (the header is row 1). */
  row: number;
  column?: string;
  /** The cell text that was rejected, when the problem is with a value. */
  value?: string;
  level: IssueLevel;
  message: string;
}

export interface ImportContext {
  /** Project currency, used when a row has none. */
  currency: string;
  /** Rows beyond this are ignored (default 20,000). */
  maxRows?: number;
}

export interface ImportResult<T> {
  records: T[];
  issues: ImportIssue[];
  rowsRead: number;
  rowsSkipped: number;
  /** Columns in the file we did not recognise (ignored). */
  unknownColumns: string[];
}

export const MAX_ROWS = 20000;

function cleanCell(col: ColumnSpec, raw: string): Cleaned<string | number | boolean> {
  switch (col.kind) {
    case "id": { const t = cleanText(raw, 40); return t.ok ? (isValidId(t.value) ? t : { ok: false, message: "may only use letters, digits, _ and -" }) : t; }
    case "text": return cleanText(raw, 300);
    case "longtext": return cleanText(raw, 2000);
    case "email": return cleanEmail(raw);
    case "phone": return cleanPhone(raw);
    case "url": return cleanUrl(raw);
    case "int": return cleanInt(raw, 0, 1000);
    case "number": {
      if (col.min !== undefined) { const n = parseNumber(raw); if (n !== null && n < col.min) return { ok: false, message: col.min === 0 ? "must not be negative" : `must be at least ${col.min}` }; }
      return cleanNumber(raw);
    }
    case "percent": return cleanNumber(raw, 0, 100);
    case "bool": return cleanBool(raw);
    case "date": return cleanDate(raw);
    case "time": return cleanTime(raw);
    case "currency": return cleanCurrency(raw);
    case "timezone": return cleanTimeZone(raw);
    case "enum": return cleanEnum(raw, col.values ?? [], col.synonyms ?? {});
  }
}

/** Checks that span columns. Returns an error to skip the row, or warnings to keep it with a field dropped. */
function crossChecks(kind: RecordKind, rec: Record<string, unknown>, warn: (column: string, message: string) => void): string | null {
  if (kind === "rooms" && typeof rec.checkIn === "string" && typeof rec.checkOut === "string" && rec.checkOut <= rec.checkIn) {
    return "Check out must be after check in";
  }
  if (kind === "guests" && typeof rec.arrival === "string" && typeof rec.departure === "string" && rec.departure < rec.arrival) {
    warn("Departure", "is before the arrival date, so it was dropped"); delete rec.departure;
  }
  if (kind === "expenses") {
    if (rec.taxType === "none" && rec.taxRate !== undefined) { warn("Tax rate %", "was ignored because the tax type is none"); delete rec.taxRate; }
    if (rec.taxType !== "none" && rec.taxRate === undefined) warn("Tax rate %", "is blank, so no tax is calculated for this row");
  }
  return null;
}

/**
 * Cleans one record. `read` returns the cell text for a column ("" when blank). Used by the CSV importer and by
 * the project-file reader so both apply exactly the same rules. Returns null when the row must be skipped.
 */
export function buildRecord(
  kind: RecordKind,
  read: (col: ColumnSpec) => string,
  ctx: ImportContext,
  row: number,
  issues: ImportIssue[],
  seen: Set<string>,
): Record<string, unknown> | null {
  const layout = LAYOUTS[kind];
  const rec: Record<string, unknown> = {};
  let skip = false;
  const warn = (column: string, message: string) => issues.push({ row, column, level: "warning", message });

  for (const col of layout.columns) {
    const raw = read(col).trim();
    if (raw === "") {
      if (col.required) { issues.push({ row, column: col.header, level: "error", message: "is required" }); skip = true; }
      else if (col.default !== undefined) rec[col.key] = col.default;
      continue;
    }
    const c = cleanCell(col, raw);
    if (c.ok) rec[col.key] = c.value;
    else if (col.required) { issues.push({ row, column: col.header, value: raw, level: "error", message: c.message }); skip = true; }
    else { issues.push({ row, column: col.header, value: raw, level: "warning", message: `${c.message}, so it was left blank` }); if (col.default !== undefined) rec[col.key] = col.default; }
  }
  if (skip) return null;

  const cross = crossChecks(kind, rec, warn);
  if (cross) { issues.push({ row, level: "error", message: cross }); return null; }

  if (layout.columns.some((c) => c.key === "currency") && rec.currency === undefined) rec.currency = ctx.currency;
  let rid = typeof rec.id === "string" ? rec.id : "";
  if (rid && seen.has(rid)) { warn("ID", "is used by an earlier row, so a new one was made"); rid = ""; }
  if (!rid) rid = newId(layout.idPrefix);
  seen.add(rid);
  rec.id = rid;
  return rec;
}

/** One line for a person: "Row 4: amount 'abc' is not a number". */
export function formatIssue(i: ImportIssue): string {
  const field = i.column ? i.column.toLowerCase() : "";
  const value = i.value !== undefined ? ` '${i.value.length > 40 ? i.value.slice(0, 40) + "…" : i.value}'` : "";
  return `Row ${i.row}: ${field ? field + value + " " : ""}${i.message}`;
}

/** Reads CSV text into records of the given kind. Never throws on bad data; problems come back as issues. */
export function importRecords<K extends RecordKind>(kind: K, text: string, ctx: ImportContext): ImportResult<RecordMap[K]> {
  const layout = LAYOUTS[kind];
  const issues: ImportIssue[] = [];
  const result: ImportResult<RecordMap[K]> = { records: [], issues, rowsRead: 0, rowsSkipped: 0, unknownColumns: [] };
  const rows = parseCsv(text);
  if (rows.length === 0) { issues.push({ row: 1, level: "error", message: "The file is empty." }); return result; }

  const byName = new Map<string, ColumnSpec>();
  for (const c of layout.columns) {
    byName.set(norm(c.header), c);
    for (const a of c.aliases ?? []) if (!byName.has(norm(a))) byName.set(norm(a), c);
  }
  const index = new Map<ColumnSpec, number>();
  rows[0].forEach((h, i) => {
    const col = byName.get(norm(h));
    if (col && !index.has(col)) index.set(col, i);
    else if (h.trim()) result.unknownColumns.push(h.trim());
  });
  const missing = layout.columns.filter((c) => c.required && !index.has(c));
  if (missing.length) {
    issues.push({ row: 1, level: "error", message: `The file is missing the required column${missing.length > 1 ? "s" : ""}: ${missing.map((c) => c.header).join(", ")}.` });
    return result;
  }

  const max = ctx.maxRows ?? MAX_ROWS;
  const seen = new Set<string>();
  const body = rows.slice(1);
  if (body.length > max) issues.push({ row: max + 2, level: "warning", message: `Only the first ${max} rows were read.` });

  body.slice(0, max).forEach((cells, i) => {
    result.rowsRead++;
    const rec = buildRecord(kind, (col) => { const at = index.get(col); return at === undefined ? "" : (cells[at] ?? ""); }, ctx, i + 2, issues, seen);
    if (rec) result.records.push(rec as unknown as RecordMap[K]);
    else result.rowsSkipped++;
  });
  return result;
}

function cellValue(v: unknown): CsvValue {
  if (v === undefined || v === null) return "";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number" || typeof v === "string") return v;
  return String(v);
}

/** Writes records as CSV with the canonical header. Cells that could run as spreadsheet formulas are neutralised. */
export function exportRecords<K extends RecordKind>(kind: K, records: readonly RecordMap[K][]): string {
  const layout = LAYOUTS[kind];
  const columns = layout.columns.map((c) => ({ key: c.key, header: c.header }));
  const rows = records.map((r) => {
    const out: Record<string, CsvValue> = {};
    for (const c of layout.columns) out[c.key] = cellValue((r as unknown as Record<string, unknown>)[c.key]);
    return out;
  });
  return toCsv(rows, columns);
}

/** A header-only CSV the person can fill in. */
export function templateCsv(kind: RecordKind): string {
  return toCsv([], LAYOUTS[kind].columns.map((c) => ({ key: c.key, header: c.header })));
}

/** Short plain-language summary line for an import. */
export function describeImport(r: ImportResult<unknown>, noun: string): string {
  const parts = [`${r.records.length} ${noun} imported`];
  if (r.rowsSkipped) parts.push(`${r.rowsSkipped} row${r.rowsSkipped === 1 ? "" : "s"} skipped`);
  const warns = r.issues.filter((i) => i.level === "warning").length;
  if (warns) parts.push(`${warns} warning${warns === 1 ? "" : "s"}`);
  return parts.join(", ") + ".";
}
